import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography,
} from "@mui/material";
import { isSuperAdmin } from "@common/domain/auth";
import {
  parseResultsFile,
  reviewHasContent,
  type ConversationReview,
  type ReviewEntry,
  type RunConversation,
  type ScenarioConversation,
} from "@common/domain/conversation-results";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import {
  deleteResultSet, deleteReview, getResultSet, listResultSets, saveReview, uploadResultSet,
  type ResultSet, type ResultSetContents,
} from "../../lib/api/ConversationResults";
import { buildReport, describeScenarioFilters, filterScenarios, type ScenarioFilters, otherReviewCounts, reviewStatusFor } from "./resultsLogic";
import ScenarioReport from "./ScenarioReport";
import TurnLog, { filterRuns, type TurnFilters } from "./TurnLog";
import ConversationDetail, { type TurnKey } from "./ConversationDetail";
import { contentKey, loadLocalReviews, saveLocalReviews } from "./localReviews";

const LOCAL_VIEWER = "local";

const DEFAULT_SCENARIO_FILTERS: ScenarioFilters = { result: "*", cat: "*", query: "" };

export default function ConversationResultsPage() {
  const { userInfo } = useUserInfoContext();
  const token = userInfo?.accessToken;
  const canManage = isSuperAdmin(userInfo?.groups);
  const [params, setParams] = useSearchParams();
  const setId = params.get("set");

  const [sets, setSets] = useState<ResultSet[] | null>(null);
  const [data, setData] = useState<ResultSetContents | null>(null);
  // A file parsed in the browser (no backend needed); shown instead of the selected result set.
  const [isLocal, setIsLocal] = useState(false);
  // Everyone else's review counts, captured when a set loads; the viewer's own review is added back as it changes.
  const [othersCounts, setOthersCounts] = useState<Record<string, number>>({});
  const [localKey, setLocalKey] = useState<string | null>(null);
  const localInput = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [view, setView] = useState<"scenario" | "turns">("scenario");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scenarioFilters, setScenarioFilters] = useState<ScenarioFilters>(DEFAULT_SCENARIO_FILTERS);
  const [turnFilters, setTurnFilters] = useState<TurnFilters>({ enabled: [], attack: "*", category: "*" });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const saving = useRef<Promise<unknown>>(Promise.resolve());

  const selectSet = useCallback((id: string | null) => {
    setParams(id ? { set: id } : {}, { replace: true });
  }, [setParams]);

  const refreshSets = useCallback(async () => {
    if (!token) return;
    try {
      setSets(await listResultSets(token));
    } catch (e) {
      const offline = e instanceof TypeError; // fetch rejects with TypeError when the server is unreachable
      setError(offline ? "Use Open file to view a CSV file" : (e as Error).message);
    }
  }, [token]);

  useEffect(() => { refreshSets(); }, [refreshSets]);

  // Default to the newest set once the list arrives.
  useEffect(() => {
    if (sets && sets.length && (!setId || !sets.some((s) => s.setId === setId))) selectSet(sets[0].setId);
  }, [sets, setId, selectSet]);

  const loadSet = useCallback(async (id: string, keepState = false) => {
    if (!token) return;
    setLoading(true);
    try {
      const contents = await getResultSet(token, id);
      setOthersCounts(otherReviewCounts(contents.reviewCounts, contents.reviews, contents.viewerId));
      setData(contents);
      if (!keepState) {
        const runs = contents.conversations.filter((c): c is RunConversation => c.kind === "run");
        setSelectedId(null);
        setScenarioFilters(DEFAULT_SCENARIO_FILTERS);
        setTurnFilters({ enabled: [...new Set(runs.map((r) => r.source))], attack: "*", category: "*" });
        setView(contents.conversations.some((c) => c.kind === "scenario") ? "scenario" : "turns");
      }
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isLocal) return;
    if (setId) loadSet(setId);
    else setData(null);
  }, [setId, loadSet, isLocal]);

  const scenarios = useMemo(() => (data?.conversations ?? []).filter((c): c is ScenarioConversation => c.kind === "scenario"), [data]);
  const runs = useMemo(() => (data?.conversations ?? []).filter((c): c is RunConversation => c.kind === "run"), [data]);
  const sources = useMemo(() => [...new Set(runs.map((r) => r.source))], [runs]);
  const reviews = data?.reviews ?? [];
  const statusFor = (id: string) => reviewStatusFor(othersCounts, reviews, data?.viewerId ?? "", id);
  const selected = data?.conversations.find((c) => c.id === selectedId) ?? null;

  const reviewsRef = useRef<ConversationReview[]>([]);
  useEffect(() => {
    reviewsRef.current = data?.reviews ?? [];
  }, [data?.reviews]);

  const openConversation = (id: string) => {
    setSelectedId(id);
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    }));
  };

  // Saves are optimistic and queued one at a time, using a ref so rapid clicks apply in order without stale closures.
  const onSave = (turn: TurnKey, patch: ReviewEntry) => {
    if (!data || !selected) return;
    const conversationId = selected.id;
    const viewerId = data.viewerId;
    const currentReviews = reviewsRef.current;
    const current = currentReviews.find((r) => r.conversationId === conversationId && r.userId === viewerId);
    const next: ConversationReview = {
      conversationId, userId: viewerId, reviewerEmail: userInfo?.email,
      turns: { ...(current?.turns ?? {}) }, overall: { ...(current?.overall ?? {}) },
    };
    if (turn === "overall") next.overall = { ...next.overall, ...patch };
    else next.turns[String(turn)] = { ...next.turns[String(turn)], ...patch };

    const updatedReviews = [
      ...currentReviews.filter((r) => !(r.conversationId === conversationId && r.userId === viewerId)),
      ...(reviewHasContent(next) ? [next] : []),
    ];
    reviewsRef.current = updatedReviews;
    setData((d) => (d ? { ...d, reviews: updatedReviews } : null));

    if (isLocal) {
      if (localKey && !saveLocalReviews(localKey, updatedReviews)) {
        setError("This browser would not save your review (storage is blocked or full). Use Export report to keep it.");
      }
      return;
    }

    const { turns, overall } = next;
    const currentSetId = data.set.setId;
    saving.current = saving.current
      .then(async () => {
        try {
          await saveReview(token, currentSetId, conversationId, { turns, overall });
        } catch (e) {
          reviewsRef.current = currentReviews;
          setData((d) => (d ? { ...d, reviews: currentReviews } : null));
          setError(`Feedback was not saved: ${(e as Error).message}`);
        }
      });
  };

  const onClear = () => {
    if (!data || !selected) return;
    const conversationId = selected.id;
    const viewerId = data.viewerId;
    const currentReviews = reviewsRef.current;
    const updatedReviews = currentReviews.filter(
      (r) => !(r.conversationId === conversationId && r.userId === viewerId)
    );
    reviewsRef.current = updatedReviews;
    setData((d) => (d ? { ...d, reviews: updatedReviews } : null));

    if (isLocal) {
      if (localKey && !saveLocalReviews(localKey, updatedReviews)) {
        setError("This browser would not save your review (storage is blocked or full). Use Export report to keep it.");
      }
      return;
    }

    const currentSetId = data.set.setId;
    saving.current = saving.current
      .then(async () => {
        try {
          await deleteReview(token, currentSetId, conversationId);
        } catch (e) {
          reviewsRef.current = currentReviews;
          setData((d) => (d ? { ...d, reviews: currentReviews } : null));
          setError(`Review was not cleared: ${(e as Error).message}`);
        }
      });
  };

  const resetViewFor = (contents: ResultSetContents) => {
    const runs = contents.conversations.filter((c): c is RunConversation => c.kind === "run");
    setSelectedId(null);
    setScenarioFilters(DEFAULT_SCENARIO_FILTERS);
    setTurnFilters({ enabled: [...new Set(runs.map((r) => r.source))], attack: "*", category: "*" });
    setView(contents.conversations.some((c) => c.kind === "scenario") ? "scenario" : "turns");
  };

  const onOpenLocal = async (file: File) => {
    try {
      const text = await file.text();
      const conversations = parseResultsFile(file.name, text);
      const key = contentKey(text);
      const saved = loadLocalReviews(key);
      const name = file.name.replace(/\.(csv|json)$/i, "");
      const contents: ResultSetContents = {
        set: {
          setId: "local", name, fileName: file.name, conversationCount: conversations.length,
          scenarioCount: conversations.filter((c) => c.kind === "scenario").length,
          runCount: conversations.filter((c) => c.kind === "run").length, uploadedBy: "",
        },
        conversations, reviews: saved, viewerId: LOCAL_VIEWER, reviewCounts: {}, reviewScope: "all",
      };
      selectSet(null);
      setIsLocal(true);
      setLocalKey(key);
      reviewsRef.current = saved;
      setOthersCounts({});
      setData(contents);
      resetViewFor(contents);
      setNotice(`Opened ${file.name} locally: ${conversations.length} conversations.${saved.length ? ` Restored ${saved.length} of your reviews from this browser.` : ""}`);
      setError(null);
    } catch (e) {
      setError(`Could not read ${file.name}: ${(e as Error).message}`);
    }
  };

  const onUpload = async (file: File) => {
    setLoading(true);
    try {
      const set = await uploadResultSet(token, file.name, await file.text());
      await refreshSets();
      selectSet(set.setId);
      setNotice(`Uploaded ${set.name}: ${set.conversationCount} conversations.`);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!data) return;
    setConfirmDelete(false);
    try {
      await deleteResultSet(token, data.set.setId);
      setNotice(`Deleted ${data.set.name}.`);
      selectSet(null);
      await refreshSets();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const exportReport = () => {
    if (!data) return;
    const md = buildReport({
      setName: data.set.name,
      scenarios,
      filteredScenarios: filterScenarios(scenarios, scenarioFilters),
      scenarioFilterLabel: describeScenarioFilters(scenarioFilters),
      runs: filterRuns(runs, turnFilters),
      sources: sources.filter((s) => turnFilters.enabled.includes(s)),
      reviews: reviewsRef.current,
      reviewScope: data.reviewScope,
    });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(new Blob([md], { type: "text/markdown" })),
      download: `conversation-report-${data.set.name.replace(/[^\w.-]+/g, "-")}-${new Date().toISOString().slice(0, 10)}.md`,
    });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <Box sx={{ maxWidth: 1320, mx: "auto", px: { xs: 2, sm: 3 }, pt: 3, pb: 7, display: "grid", gap: 2 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 400 }}>Multi-turn Conversational Results</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: "72ch" }}>
            Multi-turn conversations with an AI persona, shown turn by turn with each grader’s verdict and reasoning.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 240 }} disabled={!sets?.length}>
            <InputLabel id="result-set">Result set</InputLabel>
            <Select labelId="result-set" label="Result set" value={!isLocal && sets?.some((s) => s.setId === setId) ? setId! : ""}
              onChange={(e) => { setIsLocal(false); selectSet(e.target.value); if (e.target.value === setId) loadSet(e.target.value); }}>
              {(sets ?? []).map((s) => (
                <MenuItem key={s.setId} value={s.setId}>
                  {s.name}&nbsp;<Typography component="span" variant="caption" color="text.secondary">({s.conversationCount}{s.createdAt ? ` · ${s.createdAt.slice(0, 10)}` : ""})</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={() => localInput.current?.click()}>Open file</Button>
          <input ref={localInput} type="file" hidden accept=".csv,.json,text/csv,application/json"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onOpenLocal(f); }} />
          <Button variant="outlined" disabled={!data} onClick={exportReport}>Export report</Button>
          {canManage && (
            <>
              <Button variant="contained" onClick={() => fileInput.current?.click()} disabled={loading}>Upload results</Button>
              <input ref={fileInput} type="file" hidden accept=".csv,.json,text/csv,application/json"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }} />
              <Button color="error" disabled={!data || isLocal} onClick={() => setConfirmDelete(true)}>Delete set</Button>
            </>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      {isLocal && data && <Alert severity="info">Showing {data.set.fileName} from your computer. Reviews are saved in this browser only; use Export report to share them. Choose a result set to go back to uploaded data.</Alert>}
      {sets && !sets.length && !isLocal && (
        <Alert severity="info">
          No result sets yet.{canManage ? " Click Upload results to add a CSV or JSON results file." : " Ask a super admin to upload one."}
        </Alert>
      )}
      {loading && !data && <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>}

      {data && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={view} onChange={(_, v) => { setView(v); setSelectedId(null); }}>
              <Tab value="scenario" label={`Scenario report (${scenarios.length})`} disabled={!scenarios.length} />
              <Tab value="turns" label={`Turn log (${runs.length})`} disabled={!runs.length} />
            </Tabs>
          </Box>
          {view === "scenario" && scenarios.length > 0 && (
            <ScenarioReport scenarios={scenarios} filters={scenarioFilters} onFilters={setScenarioFilters} reviewStatus={statusFor} selectedId={selectedId} onOpen={openConversation} />
          )}
          {view === "turns" && runs.length > 0 && (
            <TurnLog runs={runs} sources={sources} filters={turnFilters} onFilters={setTurnFilters} reviewStatus={statusFor} selectedId={selectedId} onOpen={openConversation} />
          )}
          <div ref={detailRef}>
            {selected && (
              <ConversationDetail
                key={selected.id}
                conversation={selected}
                reviews={reviews.filter((r) => r.conversationId === selected.id && reviewHasContent(r))}
                reviewCount={statusFor(selected.id).count}
                showTeam={!isLocal && data.reviewScope === "all"}
                viewerId={data.viewerId}
                onSave={onSave}
                onClear={onClear}
                onClose={() => setSelectedId(null)}
                savedLocally={isLocal}
              />
            )}
          </div>
          {!isLocal && (
            <Box>
              <Button size="small" onClick={() => loadSet(data.set.setId, true)} disabled={loading}>Refresh reviews</Button>
            </Box>
          )}
        </>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {data?.set.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>This removes the result set and every reviewer’s feedback on it, for everyone. It cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={onDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
